import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { formatApiError } from "../api";
import { BrutalButton, BrutalInput } from "./Brutal";

/**
 * v403.4 — Admin log edit dialog (owner/admin ONLY, enforced server-side).
 * Editable: quantity (sale/sample rows only — backend auto-corrects the
 * holder's stock), price, customer name, date, notes. The backend snapshots
 * originals on first edit and flags the row `edited`.
 *
 * Props:
 *   row: { id, quantity, sale_price, customer_name, date, note, qtyEditable }
 *   onClose, onSaved
 */
export default function EditMovementDialog({ row, onClose, onSaved }) {
  const { t } = useTranslation();
  const [qty, setQty] = useState(row.quantity ?? "");
  const [price, setPrice] = useState(row.sale_price != null ? Number(row.sale_price).toFixed(2) : "");
  const [customer, setCustomer] = useState(row.customer_name || "");
  const [date, setDate] = useState((row.date || "").slice(0, 10));
  const [note, setNote] = useState(row.note || "");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const body = {};
    if (row.qtyEditable && qty !== "" && Number(qty) !== Number(row.quantity)) {
      body.quantity = Math.max(1, Number(qty) || 1);
    }
    if (price !== "" && Number(price) !== Number(row.sale_price ?? NaN)) {
      body.sale_price = Number(price);
    }
    if (customer.trim() && customer.trim() !== (row.customer_name || "")) {
      body.customer_name = customer.trim();
    }
    if (date && date !== (row.date || "").slice(0, 10)) {
      body.date = date;
    }
    if (note.trim() !== (row.note || "")) {
      body.note = note.trim();
    }
    if (Object.keys(body).length === 0) {
      toast.error(t("edit_log.no_changes"));
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/movement-log/${row.id}`, body);
      toast.success(t("edit_log.saved_toast"));
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="edit-movement-dialog"
        className="bg-white border border-zinc-200 rounded-xl shadow-xl p-6 w-full max-w-md space-y-3">
        <h2 className="text-xl font-semibold tracking-tight">{t("edit_log.title")}</h2>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold mb-1">{t("edit_log.qty")}</label>
            <BrutalInput type="number" min="1" value={qty}
              onChange={(e) => setQty(e.target.value)}
              disabled={!row.qtyEditable}
              title={row.qtyEditable ? undefined : t("edit_log.qty_locked")}
              data-testid="edit-qty-input" />
            {!row.qtyEditable && (
              <p className="text-[10px] text-zinc-400 mt-0.5">{t("edit_log.qty_locked")}</p>
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">{t("edit_log.price")}</label>
            <BrutalInput type="number" step="0.01" min="0" value={price}
              onChange={(e) => setPrice(e.target.value)} data-testid="edit-price-input" />
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1">{t("edit_log.customer")}</label>
          <BrutalInput value={customer} onChange={(e) => setCustomer(e.target.value)}
            data-testid="edit-customer-input" />
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1">{t("edit_log.date")}</label>
          <BrutalInput type="date" value={date} onChange={(e) => setDate(e.target.value)}
            data-testid="edit-date-input" />
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1">{t("edit_log.notes")}</label>
          <textarea rows={2} className="input-brutal w-full" value={note}
            onChange={(e) => setNote(e.target.value)} data-testid="edit-note-input" />
        </div>
        {row.qtyEditable && (
          <p className="text-[11px] text-zinc-500">{t("edit_log.stock_note")}</p>
        )}
        <div className="flex gap-3 pt-2">
          <BrutalButton type="button" variant="secondary" className="flex-1" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </BrutalButton>
          <BrutalButton type="submit" className="flex-1" disabled={busy} data-testid="edit-movement-submit">
            {busy ? t("common.saving") : t("edit_log.save")}
          </BrutalButton>
        </div>
      </form>
    </div>
  );
}
